import { parseIonicSpecies } from './ionicEquation';
import {
  ELECTRON_DISPLAY,
  FARADAY_CONSTANT,
  FARADAY_LIMITS,
  MAX_HELPER_COUNT,
  MAX_MAIN_COEFFICIENT,
  REDUCTION_POTENTIALS,
  SECONDS_PER_MINUTE,
  SPECIES,
  ELECTROLYSIS_CASES,
  UNSUPPORTED_ELECTROLYTES,
  term,
  type ElectrodeProduct,
  type ElectrolysisCase,
  type EquationSide,
  type FullEquation,
  type HalfEquation,
  type HalfEquationCase,
  type RedoxProcess,
  type ReductionCouple,
  type SpeciesId,
  type Term,
} from './electrochemicalCellsData';
import { assignOxidationStates } from './redoxAnalysis';

export * from './electrochemicalCellsData';

const POTENTIAL_DECIMALS = 100;
const FARADAY_TOLERANCE = 0.02;

export interface AtomRow {
  element: string;
  left: number;
  right: number;
}

export interface BalanceReport {
  atoms: AtomRow[];
  chargeLeft: number;
  chargeRight: number;
  atomsBalanced: boolean;
  chargeBalanced: boolean;
}

function totals(terms: Term[], electrons: number) {
  const atoms: Record<string, number> = {};
  let charge = -electrons;
  terms.forEach((term) => {
    const species = SPECIES[term.species];
    Object.entries(species.elements).forEach(([element, count]) => {
      atoms[element] = (atoms[element] ?? 0) + count * term.coefficient;
    });
    charge += species.charge * term.coefficient;
  });
  return { atoms, charge };
}

export function balanceReport(equation: HalfEquation | FullEquation): BalanceReport {
  const leftElectrons =
    'electronSide' in equation && equation.electronSide === 'left' ? equation.electrons : 0;
  const rightElectrons =
    'electronSide' in equation && equation.electronSide === 'right' ? equation.electrons : 0;
  const left = totals(equation.left, leftElectrons);
  const right = totals(equation.right, rightElectrons);
  const elements = [...new Set([...Object.keys(left.atoms), ...Object.keys(right.atoms)])];
  const atoms = elements.map((element) => ({
    element,
    left: left.atoms[element] ?? 0,
    right: right.atoms[element] ?? 0,
  }));
  return {
    atoms,
    chargeLeft: left.charge,
    chargeRight: right.charge,
    atomsBalanced: atoms.every((row) => row.left === row.right),
    chargeBalanced: left.charge === right.charge,
  };
}

function termText(term: Term): string {
  const prefix = term.coefficient === 1 ? '' : `${term.coefficient}`;
  return `${prefix}${SPECIES[term.species].display}`;
}

function electronText(count: number): string {
  return count === 1 ? ELECTRON_DISPLAY : `${count}${ELECTRON_DISPLAY}`;
}

export function halfEquationText(equation: HalfEquation): string {
  const left = equation.left.map(termText);
  const right = equation.right.map(termText);
  if (equation.electrons > 0) {
    (equation.electronSide === 'left' ? left : right).push(electronText(equation.electrons));
  }
  return `${left.join(' + ') || '—'} → ${right.join(' + ') || '—'}`;
}

export function fullEquationText(equation: FullEquation): string {
  return `${equation.left.map(termText).join(' + ')} → ${equation.right.map(termText).join(' + ')}`;
}

export function reverseHalfEquation(equation: HalfEquation): HalfEquation {
  return {
    left: equation.right,
    right: equation.left,
    electrons: equation.electrons,
    electronSide: equation.electronSide === 'left' ? 'right' : 'left',
  };
}

function gcd(a: number, b: number): number {
  return b === 0 ? Math.abs(a) : gcd(b, a % b);
}

function lcm(a: number, b: number): number {
  return (a * b) / gcd(a, b);
}

/** Adds a reduction and an oxidation half-equation after scaling so the electrons cancel. */
export function combineHalfEquations(
  reduction: HalfEquation,
  oxidation: HalfEquation,
): { equation: FullEquation; electronsTransferred: number; reductionScale: number; oxidationScale: number } {
  if (reduction.electronSide !== 'left' || oxidation.electronSide !== 'right') {
    throw new Error('combineHalfEquations needs a reduction and an oxidation half-equation');
  }
  const electronsTransferred = lcm(reduction.electrons, oxidation.electrons);
  const reductionScale = electronsTransferred / reduction.electrons;
  const oxidationScale = electronsTransferred / oxidation.electrons;
  const order: SpeciesId[] = [];
  const net = new Map<SpeciesId, number>();
  const add = (terms: Term[], scale: number, sign: number) => {
    terms.forEach((term) => {
      if (!net.has(term.species)) order.push(term.species);
      net.set(term.species, (net.get(term.species) ?? 0) + sign * term.coefficient * scale);
    });
  };
  add(reduction.left, reductionScale, -1);
  add(oxidation.left, oxidationScale, -1);
  add(reduction.right, reductionScale, 1);
  add(oxidation.right, oxidationScale, 1);
  const left: Term[] = [];
  const right: Term[] = [];
  order.forEach((species) => {
    const value = net.get(species) ?? 0;
    if (value < 0) left.push({ species, coefficient: -value });
    if (value > 0) right.push({ species, coefficient: value });
  });
  return { equation: { left, right }, electronsTransferred, reductionScale, oxidationScale };
}

function roundPotential(value: number): number {
  return Math.round(value * POTENTIAL_DECIMALS) / POTENTIAL_DECIMALS;
}

export function formatSigned(value: number, digits = 2): string {
  const fixed = Math.abs(value).toFixed(digits);
  if (Number(fixed) === 0) return (0).toFixed(digits);
  return `${value > 0 ? '+' : '−'}${fixed}`;
}

export function formatCharge(value: number): string {
  if (value === 0) return '0';
  return `${value > 0 ? '+' : '−'}${Math.abs(value)}`;
}

export interface HelperChoice {
  count: number;
  side: EquationSide;
}

export interface HalfEquationAttempt {
  process: RedoxProcess | '';
  leftCoefficient: number;
  rightCoefficient: number;
  electrons: HelperChoice;
  water: HelperChoice;
  hydrogen: HelperChoice;
}

export const EMPTY_HALF_ATTEMPT: HalfEquationAttempt = {
  process: '',
  leftCoefficient: 1,
  rightCoefficient: 1,
  electrons: { count: 0, side: 'left' },
  water: { count: 0, side: 'left' },
  hydrogen: { count: 0, side: 'left' },
};

export type HalfIssueKind = 'main' | 'oxygen' | 'hydrogen' | 'charge' | 'scale' | 'none';

export interface HalfEquationCheck {
  status: 'checked';
  equation: HalfEquation;
  report: BalanceReport;
  balanced: boolean;
  lowestTerms: boolean;
  equationCorrect: boolean;
  issue: { kind: HalfIssueKind; message: string };
  processCorrect: boolean;
  processMessage: string;
  oxidationChange: { element: string; from: number; to: number } | null;
}

export type HalfEquationCheckResult = HalfEquationCheck | { status: 'invalid'; reason: string };

function isCount(value: number, minimum: number, maximum: number): boolean {
  return Number.isInteger(value) && value >= minimum && value <= maximum;
}

export function attemptToEquation(
  item: HalfEquationCase,
  attempt: HalfEquationAttempt,
): HalfEquation {
  const left: Term[] = [term(item.main.left, attempt.leftCoefficient)];
  const right: Term[] = [term(item.main.right, attempt.rightCoefficient)];
  const place = (species: SpeciesId, choice: HelperChoice) => {
    if (choice.count > 0) (choice.side === 'left' ? left : right).push(term(species, choice.count));
  };
  place('H', attempt.hydrogen);
  place('H2O', attempt.water);
  return {
    left,
    right,
    electrons: attempt.electrons.count,
    electronSide: attempt.electrons.side,
  };
}

/** Oxidation-state change of the case's changing element, reusing the Redox Lab assignment rules. */
export function oxidationChangeFor(
  item: HalfEquationCase,
): { element: string; from: number; to: number } | null {
  const stateOf = (id: SpeciesId) => {
    const parsed = parseIonicSpecies(SPECIES[id].ascii).species;
    if (!parsed) return null;
    const assignment = assignOxidationStates(parsed);
    const value = assignment.states[item.changingElement];
    return assignment.complete && value !== undefined ? value : null;
  };
  const from = stateOf(item.main.left);
  const to = stateOf(item.main.right);
  if (from === null || to === null) return null;
  return { element: item.changingElement, from, to };
}

function sideCount(rows: AtomRow[], element: string) {
  return rows.find((row) => row.element === element) ?? { element, left: 0, right: 0 };
}

function processMessageFor(
  item: HalfEquationCase,
  process: RedoxProcess,
  change: ReturnType<typeof oxidationChangeFor>,
): string {
  const changeText = change
    ? `${change.element} goes from ${formatCharge(change.from)} to ${formatCharge(change.to)}`
    : `${item.changingElement} changes oxidation state`;
  if (process === item.process) {
    return item.process === 'reduction'
      ? `Yes, reduction: ${changeText}, so electrons are gained and appear on the left.`
      : `Yes, oxidation: ${changeText}, so electrons are lost and appear on the right.`;
  }
  return item.process === 'reduction'
    ? `Not oxidation: ${changeText}. A falling oxidation state means electrons are gained, which is reduction.`
    : `Not reduction: ${changeText}. A rising oxidation state means electrons are lost, which is oxidation.`;
}

export function checkHalfEquationAttempt(
  item: HalfEquationCase,
  attempt: HalfEquationAttempt,
): HalfEquationCheckResult {
  if (!attempt.process) return { status: 'invalid', reason: 'Choose oxidation or reduction first.' };
  const coefficientsValid =
    isCount(attempt.leftCoefficient, 1, MAX_MAIN_COEFFICIENT) &&
    isCount(attempt.rightCoefficient, 1, MAX_MAIN_COEFFICIENT);
  const helpersValid = [attempt.electrons, attempt.water, attempt.hydrogen].every((helper) =>
    isCount(helper.count, 0, MAX_HELPER_COUNT),
  );
  if (!coefficientsValid || !helpersValid) {
    return {
      status: 'invalid',
      reason: `Use whole numbers: coefficients 1–${MAX_MAIN_COEFFICIENT}, helper counts 0–${MAX_HELPER_COUNT}.`,
    };
  }
  if (item.medium === 'simple' && (attempt.water.count > 0 || attempt.hydrogen.count > 0)) {
    return {
      status: 'invalid',
      reason: 'This half-equation has no oxygen or hydrogen to balance, so H₂O and H⁺ are not used.',
    };
  }

  const equation = attemptToEquation(item, attempt);
  const report = balanceReport(equation);
  const balanced = report.atomsBalanced && report.chargeBalanced;
  const divisor = [
    attempt.leftCoefficient,
    attempt.rightCoefficient,
    attempt.electrons.count,
    attempt.water.count,
    attempt.hydrogen.count,
  ].reduce((current, value) => (value === 0 ? current : gcd(current, value)), 0);
  const lowestTerms = divisor <= 1;
  const change = oxidationChangeFor(item);

  let issue: HalfEquationCheck['issue'] = {
    kind: 'none',
    message: 'Atoms and charge balance in the lowest whole-number ratio.',
  };
  const mainRow = sideCount(report.atoms, item.changingElement);
  const oxygen = sideCount(report.atoms, 'O');
  const hydrogen = sideCount(report.atoms, 'H');
  if (mainRow.left !== mainRow.right) {
    issue = {
      kind: 'main',
      message: `${item.changingElement}: ${mainRow.left} on the left but ${mainRow.right} on the right. Balance the element that changes first by adjusting the coefficients.`,
    };
  } else if (oxygen.left !== oxygen.right) {
    issue = {
      kind: 'oxygen',
      message: `Oxygen: ${oxygen.left} on the left but ${oxygen.right} on the right. In acidic solution, add H₂O to the side that is short of oxygen.`,
    };
  } else if (hydrogen.left !== hydrogen.right) {
    issue = {
      kind: 'hydrogen',
      message: `Hydrogen: ${hydrogen.left} on the left but ${hydrogen.right} on the right. Add H⁺ to the side that is short of hydrogen.`,
    };
  } else if (!report.chargeBalanced) {
    issue = {
      kind: 'charge',
      message: `Charge: ${formatCharge(report.chargeLeft)} on the left but ${formatCharge(report.chargeRight)} on the right. Add electrons to the more positive side until the totals match.`,
    };
  } else if (!lowestTerms) {
    issue = {
      kind: 'scale',
      message: `Balanced, but every number shares a factor of ${divisor}. Divide through to write the lowest whole-number ratio.`,
    };
  }

  return {
    status: 'checked',
    equation,
    report,
    balanced,
    lowestTerms,
    equationCorrect: balanced && lowestTerms,
    issue,
    processCorrect: attempt.process === item.process,
    processMessage: processMessageFor(item, attempt.process, change),
    oxidationChange: change,
  };
}

export function findCouple(id: string): ReductionCouple | undefined {
  return REDUCTION_POTENTIALS.find((entry) => entry.id === id);
}

export const BUILDABLE_COUPLES = REDUCTION_POTENTIALS.filter(
  (entry) => entry.galvanicUse === 'buildable',
);

export interface GalvanicCell {
  status: 'ok';
  cathode: ReductionCouple;
  anode: ReductionCouple;
  eCellV: number;
  spontaneous: boolean;
  overall: FullEquation;
  electronsTransferred: number;
  cathodeHalf: HalfEquation;
  anodeHalf: HalfEquation;
}

export type GalvanicOutcome = GalvanicCell | { status: 'unsupported'; reason: string };

/** E°cell for a stated assignment: E°cathode − E°anode, rounded to 0.01 V. */
export function cellPotential(cathode: ReductionCouple, anode: ReductionCouple): number {
  return roundPotential(cathode.e0V - anode.e0V);
}

export function buildGalvanicCell(firstId: string, secondId: string): GalvanicOutcome {
  const first = findCouple(firstId);
  const second = findCouple(secondId);
  if (!first || !second) {
    return { status: 'unsupported', reason: 'Choose two half-cells from the reviewed table.' };
  }
  if (first.id === second.id) {
    return {
      status: 'unsupported',
      reason: 'Two identical standard half-cells give no voltage. Choose two different half-cells.',
    };
  }
  const ranking = [first, second].find((entry) => entry.galvanicUse === 'ranking-only');
  if (ranking) {
    return {
      status: 'unsupported',
      reason: `${ranking.label} is in the table for ranking only. ${ranking.note ?? ''}`.trim(),
    };
  }
  if (first.e0V === second.e0V) {
    return {
      status: 'unsupported',
      reason: 'These half-cells have the same standard potential, so the model predicts no net reaction.',
    };
  }
  const [cathode, anode] = first.e0V > second.e0V ? [first, second] : [second, first];
  const anodeHalf = reverseHalfEquation(anode.reduction);
  const combined = combineHalfEquations(cathode.reduction, anodeHalf);
  const eCellV = cellPotential(cathode, anode);
  return {
    status: 'ok',
    cathode,
    anode,
    eCellV,
    spontaneous: eCellV > 0,
    overall: combined.equation,
    electronsTransferred: combined.electronsTransferred,
    cathodeHalf: cathode.reduction,
    anodeHalf,
  };
}

export interface CathodePredictionFeedback {
  correct: boolean;
  assignedECellV: number;
  message: string;
}

export function evaluateCathodePrediction(
  cell: GalvanicCell,
  predictedCathodeId: string,
): CathodePredictionFeedback {
  if (predictedCathodeId === cell.cathode.id) {
    return {
      correct: true,
      assignedECellV: cell.eCellV,
      message: `Correct. ${cell.cathode.label} has the higher reduction potential (${formatSigned(cell.cathode.e0V)} V), so it is reduced at the cathode. E°cell = ${formatSigned(cell.cathode.e0V)} − (${formatSigned(cell.anode.e0V)}) = ${formatSigned(cell.eCellV)} V, which is positive, so the cell is spontaneous.`,
    };
  }
  const assigned = cellPotential(cell.anode, cell.cathode);
  return {
    correct: false,
    assignedECellV: assigned,
    message: `With ${cell.anode.label} as the cathode, E°cell = ${formatSigned(cell.anode.e0V)} − (${formatSigned(cell.cathode.e0V)}) = ${formatSigned(assigned)} V. A negative E°cell means that direction is not spontaneous. The half-cell with the higher (more positive) E°, ${cell.cathode.label}, is reduced, so it is the cathode.`,
  };
}

export function checkCellPotentialAnswer(
  cell: GalvanicCell,
  answerV: number,
): { status: 'invalid' | 'correct' | 'sign' | 'sum' | 'wrong'; message: string } {
  if (!Number.isFinite(answerV)) {
    return { status: 'invalid', message: 'Enter E°cell as a number of volts, for example 1.10.' };
  }
  const tolerance = 1 / POTENTIAL_DECIMALS / 2;
  if (Math.abs(answerV - cell.eCellV) <= tolerance) {
    return { status: 'correct', message: `Correct: E°cell = ${formatSigned(cell.eCellV)} V.` };
  }
  if (Math.abs(answerV + cell.eCellV) <= tolerance) {
    return {
      status: 'sign',
      message: 'The size is right but the sign is reversed. Use E°cathode − E°anode with the cathode as the higher E°.',
    };
  }
  if (Math.abs(answerV - roundPotential(cell.cathode.e0V + cell.anode.e0V)) <= tolerance) {
    return {
      status: 'sum',
      message: 'You added the two reduction potentials. Subtract instead: E°cathode − E°anode. Do not change the sign of the anode value first.',
    };
  }
  return {
    status: 'wrong',
    message: `Not yet. Use E°cathode − E°anode = ${formatSigned(cell.cathode.e0V)} − (${formatSigned(cell.anode.e0V)}).`,
  };
}

export function findElectrolysisCase(
  id: string,
): { status: 'ok'; item: ElectrolysisCase } | { status: 'unsupported'; reason: string } {
  const item = ELECTROLYSIS_CASES.find((entry) => entry.id === id);
  if (item) return { status: 'ok', item };
  const known = UNSUPPORTED_ELECTROLYTES.find((entry) => entry.id === id);
  return {
    status: 'unsupported',
    reason:
      known?.reason ??
      'This electrolyte is not in the reviewed case list. Aqueous products are predicted only for reviewed cases.',
  };
}

export interface ElectrodePredictionFeedback {
  cathodeCorrect: boolean;
  anodeCorrect: boolean;
  cathodeMessage: string;
  anodeMessage: string;
}

export function evaluateElectrodePrediction(
  item: ElectrolysisCase,
  cathodeId: string,
  anodeId: string,
): ElectrodePredictionFeedback {
  const labelOf = (options: ElectrodeProduct[], id: string) =>
    options.find((option) => option.id === id)?.label ?? 'that product';
  const cathodeCorrect = cathodeId === item.cathode.productId;
  const anodeCorrect = anodeId === item.anode.productId;
  return {
    cathodeCorrect,
    anodeCorrect,
    cathodeMessage: cathodeCorrect
      ? `Correct. ${item.cathode.reason}`
      : `Not ${labelOf(item.cathodeOptions, cathodeId).toLowerCase()}. ${item.cathode.reason}`,
    anodeMessage: anodeCorrect
      ? `Correct. ${item.anode.reason}`
      : `Not ${labelOf(item.anodeOptions, anodeId).toLowerCase()}. ${item.anode.reason}`,
  };
}

export interface FaradayResult {
  status: 'ok';
  chargeC: number;
  electronMol: number;
  productMol: number;
  massG: number;
}

/** n(e⁻) = I·t / F, n(product) = n(e⁻) ÷ electrons per formula, m = n × M. Assumes 100% current efficiency. */
export function calculateFaraday(
  item: ElectrolysisCase,
  currentA: number,
  timeMin: number,
): FaradayResult | { status: 'invalid'; reason: string } {
  const inRange =
    Number.isFinite(currentA) &&
    Number.isFinite(timeMin) &&
    currentA >= FARADAY_LIMITS.minCurrentA &&
    currentA <= FARADAY_LIMITS.maxCurrentA &&
    timeMin >= FARADAY_LIMITS.minTimeMin &&
    timeMin <= FARADAY_LIMITS.maxTimeMin;
  if (!inRange) {
    return {
      status: 'invalid',
      reason: `Use a current of ${FARADAY_LIMITS.minCurrentA}–${FARADAY_LIMITS.maxCurrentA} A and a time of ${FARADAY_LIMITS.minTimeMin}–${FARADAY_LIMITS.maxTimeMin} minutes.`,
    };
  }
  const chargeC = currentA * timeMin * SECONDS_PER_MINUTE;
  const electronMol = chargeC / FARADAY_CONSTANT;
  const productMol = electronMol / item.faraday.electronsPerFormula;
  return { status: 'ok', chargeC, electronMol, productMol, massG: productMol * item.faraday.molarMassGmol };
}

function closeTo(value: number, target: number): boolean {
  return target !== 0 && Math.abs(value - target) / Math.abs(target) <= FARADAY_TOLERANCE;
}

export function checkFaradayMass(
  item: ElectrolysisCase,
  result: FaradayResult,
  answerG: number,
): { status: 'invalid' | 'correct' | 'minutes' | 'ratio' | 'wrong'; message: string } {
  if (!Number.isFinite(answerG) || answerG <= 0) {
    return { status: 'invalid', message: 'Enter the mass in grams as a positive number.' };
  }
  if (closeTo(answerG, result.massG)) {
    return {
      status: 'correct',
      message: `Correct: about ${result.massG.toPrecision(3)} g of ${item.faraday.productLabel}.`,
    };
  }
  if (closeTo(answerG, result.massG / SECONDS_PER_MINUTE)) {
    return {
      status: 'minutes',
      message: 'Your answer is 60 times too small. Convert minutes to seconds before using Q = I·t.',
    };
  }
  if (
    item.faraday.electronsPerFormula !== 1 &&
    closeTo(answerG, result.electronMol * item.faraday.molarMassGmol)
  ) {
    return {
      status: 'ratio',
      message: `You used n(e⁻) as the amount of ${item.faraday.productLabel}. Each formula unit needs ${item.faraday.electronsPerFormula} electrons, so divide n(e⁻) by ${item.faraday.electronsPerFormula}.`,
    };
  }
  return {
    status: 'wrong',
    message: `Not yet. Work in steps: Q = I·t (seconds), n(e⁻) = Q ÷ ${FARADAY_CONSTANT}, then divide by ${item.faraday.electronsPerFormula} and multiply by ${item.faraday.molarMassGmol} g mol⁻¹.`,
  };
}
